import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { supabase } from '../supabase';
import { collection, getDocs } from 'firebase/firestore';
import syncService from '../services/databaseSync';

// Custom status entries: each can have a custom firebase counter function
const TABLE_MAP = [
    {
        label: 'Customers (users)',
        supabase: 'customers',
        // Only count Firebase users that are NOT admins
        getFirebaseCount: async () => {
            const snap = await getDocs(collection(db, 'users'));
            return snap.docs.filter(d => {
                const t = (d.data().userType || d.data().role || '').toLowerCase();
                return t !== 'admin' && t !== 'superadmin';
            }).length;
        },
    },
    {
        label: 'Admins',
        supabase: 'admins',
        // Admins live in Firebase 'users' collection with role=admin,
        // plus any docs in the separate 'admins' collection
        getFirebaseCount: async () => {
            const adminIds = new Set();
            try {
                const usersSnap = await getDocs(collection(db, 'users'));
                usersSnap.docs.forEach(d => {
                    const t = (d.data().userType || d.data().role || '').toLowerCase();
                    if (t === 'admin' || t === 'superadmin') {
                        adminIds.add(d.id);
                    }
                });
            } catch (_) { }
            try {
                const adminsSnap = await getDocs(collection(db, 'admins'));
                adminsSnap.docs.forEach(d => {
                    adminIds.add(d.id);
                });
            } catch (_) { }
            return adminIds.size;
        },
        getSupabaseCount: async () => {
            const { data, error } = await supabase
                .from('admins')
                .select('firebase_id');

            if (error) throw error;

            return new Set(
                (data || [])
                    .map(row => row.firebase_id)
                    .filter(Boolean)
            ).size;
        },
    },
    {
        label: 'Service Providers',
        supabase: 'serviceProviders',
        firebase: 'serviceProviders',
    },
    {
        label: 'Bookings',
        supabase: 'bookings',
        firebase: 'bookings',
    },
    {
        label: 'Reviews',
        supabase: 'reviews',
        firebase: 'reviews',
    },
];

const MigrationPage = () => {
    const [status, setStatus] = useState({});   // { tableName: { firebase: N, supabase: N, error: '' } }
    const [logs, setLogs] = useState([]);
    const [migrating, setMigrating] = useState(false);
    const [checking, setChecking] = useState(false);

    const addLog = (msg, type = 'info') => {
        const time = new Date().toLocaleTimeString();
        setLogs(prev => [{ time, msg, type }, ...prev]);
    };

    const checkStatus = useCallback(async () => {
        setChecking(true);
        addLog('Checking database status...');
        const result = {};

        for (const entry of TABLE_MAP) {
            const { label, supabase: sbTable, firebase: fbCollection, getFirebaseCount, getSupabaseCount } = entry;
            const row = { firebase: '?', supabase: '?', firebaseError: '', supabaseError: '' };

            // Firebase count
            try {
                if (getFirebaseCount) {
                    row.firebase = await getFirebaseCount();
                } else {
                    const snap = await getDocs(collection(db, fbCollection));
                    row.firebase = snap.size;
                }
            } catch (err) {
                row.firebaseError = err.message;
                row.firebase = 'Error';
            }

            // Supabase count
            try {
                if (getSupabaseCount) {
                    row.supabase = await getSupabaseCount();
                } else {
                    const { count, error } = await supabase
                        .from(sbTable)
                        .select('*', { count: 'exact', head: true });
                    if (error) {
                        row.supabaseError = error.message;
                        row.supabase = 'Error';
                    } else {
                        row.supabase = count;
                    }
                }
            } catch (err) {
                row.supabaseError = err.message;
                row.supabase = 'Error';
            }

            result[sbTable] = row;
            addLog(
                `${label}: Firebase=${row.firebase}  |  Supabase=${row.supabase}${row.supabaseError ? '  ⚠️ ' + row.supabaseError : ''}`,
                row.supabaseError ? 'error' : 'info'
            );
        }

        setStatus(result);
        setChecking(false);
    }, []);

    useEffect(() => {
        checkStatus();
    }, [checkStatus]);

    const forceMigrate = async () => {
        setMigrating(true);
        addLog('🚀 Starting forced migration: Firebase → Supabase', 'start');

        // Intercept console output to show in our log panel
        const origLog = console.log;
        const origErr = console.error;
        const origWarn = console.warn;
        console.log = (...args) => { addLog(args.join(' '), 'info'); origLog(...args); };
        console.error = (...args) => { addLog(args.join(' '), 'error'); origErr(...args); };
        console.warn = (...args) => { addLog(args.join(' '), 'warn'); origWarn(...args); };

        try {
            await syncService.migrateFirebaseToSupabase();
            addLog('✅ Migration finished! Refreshing status...', 'success');
            await checkStatus();
        } catch (err) {
            addLog('❌ Migration error: ' + err.message, 'error');
        } finally {
            console.log = origLog;
            console.error = origErr;
            console.warn = origWarn;
            setMigrating(false);
        }
    };

    const getRowColor = (entry) => {
        if (!entry) return '';
        if (entry.supabase === 'Error') return '#fff3cd';
        if (entry.supabase === 0 && entry.firebase > 0) return '#f8d7da';
        if (entry.supabase === entry.firebase) return '#d1e7dd';
        return '#fff3cd';
    };

    const getLogColor = (type) => {
        if (type === 'error') return '#dc3545';
        if (type === 'warn') return '#ffc107';
        if (type === 'success') return '#198754';
        if (type === 'start') return '#0d6efd';
        return '#212529';
    };

    return (
        <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
            <h1 style={{ marginBottom: 4 }}>Database Sync Status</h1>
            <p style={{ color: '#666', marginBottom: 24 }}>
                Compare Firebase vs Supabase row counts and force a migration if Supabase is empty.
            </p>

            {/* Status Table */}
            <div style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, overflow: 'hidden', marginBottom: 24 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ background: '#f8f9fa' }}>
                            <th style={thStyle}>Collection / Table</th>
                            <th style={thStyle}>Firebase (rows)</th>
                            <th style={thStyle}>Supabase (rows)</th>
                            <th style={thStyle}>State</th>
                        </tr>
                    </thead>
                    <tbody>
                        {TABLE_MAP.map(({ supabase: sbTable, label }) => {
                            const entry = status[sbTable];
                            const synced = entry && entry.firebase === entry.supabase && typeof entry.firebase === 'number';
                            const hasError = entry && (entry.supabaseError || entry.firebaseError);
                            const isEmpty = entry && entry.supabase === 0 && entry.firebase > 0;
                            return (
                                <tr key={sbTable} style={{ background: getRowColor(entry), borderTop: '1px solid #dee2e6' }}>
                                    <td style={tdStyle}><code>{label}</code></td>
                                    <td style={tdStyle}>{entry ? entry.firebase : '...'}</td>
                                    <td style={tdStyle}>{entry ? entry.supabase : '...'}</td>
                                    <td style={tdStyle}>
                                        {!entry && '⏳ Checking...'}
                                        {entry && hasError && '❌ Error — check schema'}
                                        {entry && isEmpty && '⚠️ Supabase empty'}
                                        {entry && synced && '✅ In sync'}
                                        {entry && !hasError && !isEmpty && !synced && typeof entry.supabase === 'number' && '⚠️ Out of sync'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Schema Fix Notice */}
            <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8, padding: 16, marginBottom: 24 }}>
                <strong>⚠️ Getting "permission-denied" errors?</strong> Your Firebase Security Rules need to be updated to allow admin writes.
                Go to <a href="https://console.firebase.google.com" target="_blank" rel="noreferrer">Firebase Console</a> → Firestore → Rules, and paste the contents of the <code>firestore.rules</code> file from the project root, then click <strong>Publish</strong>.
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
                <button
                    onClick={checkStatus}
                    disabled={checking || migrating}
                    style={btnStyle('#0d6efd')}
                >
                    {checking ? '⏳ Checking...' : '🔄 Refresh Status'}
                </button>
                <button
                    onClick={forceMigrate}
                    disabled={migrating || checking}
                    style={btnStyle('#198754')}
                >
                    {migrating ? '⏳ Syncing...' : '🔄 Force Full Two-Way Sync'}
                </button>
                <button
                    onClick={() => setLogs([])}
                    disabled={migrating}
                    style={btnStyle('#6c757d')}
                >
                    Clear Logs
                </button>
            </div>

            {/* Live Log Panel */}
            <div style={{ background: '#212529', borderRadius: 8, padding: 16, minHeight: 200, maxHeight: 400, overflowY: 'auto' }}>
                <div style={{ color: '#adb5bd', marginBottom: 8, fontSize: 12 }}>Live Logs</div>
                {logs.length === 0 && <div style={{ color: '#6c757d' }}>No logs yet...</div>}
                {logs.map((log, i) => (
                    <div key={i} style={{ fontFamily: 'monospace', fontSize: 12, color: getLogColor(log.type), marginBottom: 2 }}>
                        <span style={{ color: '#6c757d' }}>[{log.time}] </span>{log.msg}
                    </div>
                ))}
            </div>
        </div>
    );
};

const thStyle = { padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: 14 };
const tdStyle = { padding: '12px 16px', fontSize: 14 };
const btnStyle = (bg) => ({
    background: bg,
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '10px 20px',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 14,
    opacity: 1,
});

export default MigrationPage;
